import { ParamMap, Params } from '@angular/router';

/** Pagination et tri d'un tableau tels que persistés dans l'URL (PRED-3054/3175). `pageIndex` commence à 1. */
export interface ListPagingState {
  pageIndex: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: string;
}

export type ListPagingDefaults = Partial<Omit<ListPagingState, 'pageIndex'>>;

/**
 * Clés d'URL d'un tableau. Sans préfixe : `page`, `pageSize`, `sort` (listing pleine page).
 * Un préfixe isole un tableau qui partage l'URL avec d'autres, ex. l'onglet catalogue d'une demande,
 * dont les onglets voisins conservent les query params : `catalogPage`, `catalogPageSize`, `catalogSort`.
 */
function listPagingKeys(prefix: string): { page: string; pageSize: string; sort: string } {
  const key = (name: string) => (prefix ? `${prefix}${name[0].toUpperCase()}${name.slice(1)}` : name);
  return { page: key('page'), pageSize: key('pageSize'), sort: key('sort') };
}

/** Lit l'état depuis l'URL : toute valeur absente ou invalide laisse celle de `current`. */
export function readListPagingParams(params: ParamMap, current: ListPagingState, prefix = ''): ListPagingState {
  const keys = listPagingKeys(prefix);
  const state = { ...current };

  const page = Number(params.get(keys.page));
  if (Number.isInteger(page) && page > 1) {
    state.pageIndex = page;
  }

  const pageSize = Number(params.get(keys.pageSize));
  if (Number.isInteger(pageSize) && pageSize > 0) {
    state.pageSize = pageSize;
  }

  const [sortField, sortOrder] = (params.get(keys.sort) ?? '').split(':');
  if (sortField && (sortOrder === 'asc' || sortOrder === 'desc')) {
    state.sortField = sortField;
    state.sortOrder = sortOrder;
  }

  return state;
}

/**
 * Query params reflétant l'état. Les valeurs par défaut valent `null` (clé retirée de l'URL)
 * pour qu'un même état produise toujours la même URL.
 */
export function writeListPagingParams(state: ListPagingState, defaults: ListPagingDefaults, prefix = ''): Params {
  const keys = listPagingKeys(prefix);
  const isDefaultSort = state.sortField === defaults.sortField && state.sortOrder === defaults.sortOrder;

  return {
    [keys.page]: state.pageIndex > 1 ? state.pageIndex : null,
    [keys.pageSize]: state.pageSize !== defaults.pageSize ? state.pageSize : null,
    [keys.sort]: state.sortField && !isDefaultSort ? `${state.sortField}:${state.sortOrder}` : null,
  };
}
